import type { IRoleRepository } from '@src/domain/rbac/repository/role_repository';
import type { IPermissionRepository } from '@src/domain/rbac/repository/permission_repository';
import { ListAllRolesUseCase } from './list_all_roles.usecase';
import { GetRoleByIdUseCase } from './get_role_by_id.usecase';
import { CreateRoleUseCase } from './create_role.usecase';
import { UpdateRoleUseCase } from './update_role.usecase';
import { DeleteRoleUseCase } from './delete_role.usecase';
import { AssignPermissionsToRoleUseCase } from './assign_permissions_to_role.usecase';
import { RemovePermissionsFromRoleUseCase } from './remove_permissions_from_role.usecase';
import { AssignRoleToUserUseCase } from './assign_role_to_user.usecase';
import { AssignRolesToUserUseCase } from './assign_roles_to_user.usecase';
import { RemoveRolesFromUserUseCase } from './remove_roles_from_user.usecase';
import { ListAllPermissionsUseCase } from './list_all_permissions.usecase';
import { GetPermissionByIdUseCase } from './get_permission_by_id.usecase';
import { UpdatePermissionUseCase } from './update_permission.usecase';

/**
 * RBAC Service
 * Aggregates all RBAC-related use cases
 */
export class RbacService {
    // Role use cases
    public readonly listAllRoles: ListAllRolesUseCase;
    public readonly getRoleById: GetRoleByIdUseCase;
    public readonly createRole: CreateRoleUseCase;
    public readonly updateRole: UpdateRoleUseCase;
    public readonly deleteRole: DeleteRoleUseCase;
    public readonly assignPermissionsToRole: AssignPermissionsToRoleUseCase;
    public readonly removePermissionsFromRole: RemovePermissionsFromRoleUseCase;
    public readonly assignRoleToUser: AssignRoleToUserUseCase;
    public readonly assignRolesToUser: AssignRolesToUserUseCase;
    public readonly removeRolesFromUser: RemoveRolesFromUserUseCase;

    // Permission use cases
    public readonly listAllPermissions: ListAllPermissionsUseCase;
    public readonly getPermissionById: GetPermissionByIdUseCase;
    public readonly updatePermission: UpdatePermissionUseCase;

    constructor(
        roleRepository: IRoleRepository,
        permissionRepository: IPermissionRepository
    ) {
        // Initialize role use cases
        this.listAllRoles = new ListAllRolesUseCase(roleRepository);
        this.getRoleById = new GetRoleByIdUseCase(roleRepository);
        this.createRole = new CreateRoleUseCase(roleRepository);
        this.updateRole = new UpdateRoleUseCase(roleRepository);
        this.deleteRole = new DeleteRoleUseCase(roleRepository);
        this.assignPermissionsToRole = new AssignPermissionsToRoleUseCase(roleRepository);
        this.removePermissionsFromRole = new RemovePermissionsFromRoleUseCase(roleRepository);
        this.assignRoleToUser = new AssignRoleToUserUseCase(roleRepository);
        this.assignRolesToUser = new AssignRolesToUserUseCase(roleRepository);
        this.removeRolesFromUser = new RemoveRolesFromUserUseCase(roleRepository);

        // Initialize permission use cases
        this.listAllPermissions = new ListAllPermissionsUseCase(permissionRepository);
        this.getPermissionById = new GetPermissionByIdUseCase(permissionRepository);
        this.updatePermission = new UpdatePermissionUseCase(permissionRepository);
    }
}
